import { type CSSProperties, type JSX, useCallback, useEffect, useRef, useState } from 'react';

import type { MobiLauncherConfig as Config } from '@schema/index.js';

import { apiBaseUrl, listDevices, saveTenantConfig, type SaveConfigResult } from '../api.js';
import {
  deriveSubnetOptions,
  PilotSubnetsContext,
  type SubnetOption,
} from './pilot-subnets.js';
import { deriveTagOptions, PilotTagsContext } from './pilot-tags.js';
import { AdminAccessEditor } from './components/AdminAccessEditor.js';
import { HeroEditor } from './components/HeroEditor.js';
import { ToolsEditor } from './components/ToolsEditor.js';
import { FeaturesEditor } from './components/FeaturesEditor.js';
import { OemDeviceEditor } from './components/OemDeviceEditor.js';
import { PreviewPane, type JumpTarget } from './components/PreviewPane.js';
import { ProfileBar } from './components/ProfileBar.js';
import { ProfileBasicsEditor } from './components/ProfileBasicsEditor.js';
import { RemoteConfigEditor } from './components/RemoteConfigEditor.js';
import { SchedulesEditor } from './components/SchedulesEditor.js';
import { SignageEditor } from './components/SignageEditor.js';
import { ShortcutsHelp } from './components/ShortcutsHelp.js';
import { loadPreviewWidth, savePreviewWidth, Splitter } from './components/Splitter.js';
import { StatusBarEditor } from './components/StatusBarEditor.js';
import { TelemetryEditor } from './components/TelemetryEditor.js';
import { ThemeEditor } from './components/ThemeEditor.js';
import { TileGridEditor } from './components/TileGridEditor.js';
import { Toolbar } from './components/Toolbar.js';
import './styles.css';
import { useBuilderStore } from './state.js';

type SectionId =
  | 'profile-basics'
  | 'tiles'
  | 'hero'
  | 'tools'
  | 'theme'
  | 'status-bar'
  | 'admin'
  | 'features'
  | 'schedules'
  | 'signage'
  | 'device'
  | 'telemetry'
  | 'remote';

type Tier = 'core' | 'pro' | 'enterprise';

interface SectionDef {
  id: SectionId;
  label: string;
  status: 'ready' | 'planned';
  /**
   * Tier required to APPLY these settings on-device. Sections below the
   * customer's tier render with a `<TierLockBanner>` at the top + a
   * dimmed opacity treatment. Editing stays enabled so the live preview
   * still updates — Free tenants can still see what Pro looks like.
   */
  requires: Tier;
}

const TIER_RANK: Record<Tier, number> = { core: 0, pro: 1, enterprise: 2 };
const TIER_LABEL: Record<Tier, string> = {
  core: 'Core',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

/**
 * Section order follows the customer journey:
 *   look + feel first (basics → theme → status bar → hero → tile grid)
 *   → operational (features → admin → schedules)
 *   → enterprise-tier (device → remote → telemetry).
 * The tier hints in the labels are advisory only — runtime gates
 * by license, not by editor visibility (Phase 2 will dim disabled
 * sections explicitly).
 */
const SECTIONS: SectionDef[] = [
  { id: 'profile-basics', label: 'Profile basics',          status: 'ready', requires: 'core' },
  { id: 'features',       label: 'Features',                status: 'ready', requires: 'core' },
  { id: 'theme',          label: 'Theme',                   status: 'ready', requires: 'core' },
  { id: 'status-bar',     label: 'Status bar',              status: 'ready', requires: 'core' },
  { id: 'hero',           label: 'Hero',                    status: 'ready', requires: 'core' },
  { id: 'tiles',          label: 'Tile grid',               status: 'ready', requires: 'core' },
  { id: 'tools',          label: 'Tools (Pro)',             status: 'ready', requires: 'pro' },
  // Admin access groups the exit gesture / PIN / diagnostic overlay /
  // audit-log toggles. The runtime gates the gesture + overlay + audit
  // behind their respective Pro/Ent feature flags, so the whole
  // section is gated at Pro (and the Audit toggle inside the section
  // further locks until the auditLog Enterprise flag is on).
  { id: 'admin',          label: 'Admin access (Pro)',      status: 'ready', requires: 'pro' },
  { id: 'schedules',      label: 'Schedules (Pro)',         status: 'ready', requires: 'pro' },
  { id: 'signage',        label: 'Signage (Pro)',           status: 'ready', requires: 'pro' },
  // OEM device-extensions editor (Zebra / Samsung / Honeywell) is
  // hidden from the section nav until certification lands on physical
  // hardware. The schema + runtime still accept `device:` config and
  // the `oem.*` feature flags for any tenant that already has them.
  // Restore by uncommenting once a vendor is validated end-to-end.
  // { id: 'device',         label: 'Device extensions (Ent)', status: 'ready', requires: 'enterprise' },
  { id: 'remote',         label: 'Remote config (Ent)',     status: 'ready', requires: 'enterprise' },
  { id: 'telemetry',      label: 'Telemetry (Ent)',         status: 'ready', requires: 'enterprise' },
];

/** ms to wait after the last edit before auto-saving. */
const SAVE_DEBOUNCE_MS = 800;

type SaveStatus =
  | { kind: 'idle' }
  /** Edit captured locally, debounce ticking; PUT not yet in flight. */
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: number }
  | { kind: 'error'; message: string };

export interface BuilderEditorProps {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (writes via the session cookie). */
  editToken: string | null;
  initialConfig: Config;
  /**
   * Customer's current tier — drives the section-level lock UX. Sections
   * with `requires > currentTier` render a TierLockBanner above their
   * content + dim the panel; editing stays enabled (the live preview
   * still simulates Pro themes for Free customers).
   */
  currentTier: Tier;
  /**
   * Click handler for the lock banner's "Upgrade" CTA — the Portal
   * switches to its Upgrade tab.
   */
  onUpgradeClick: () => void;
  /**
   * Bubbles up the LivePreview iframe's `preview-ready` postMessage.
   * The Portal-level first-run tour gates its `run` prop on this so
   * step 4 ("Live preview") doesn't fire against an empty iframe.
   * Lives above BuilderEditor so the tour survives outer-tab changes
   * (clicking the Devices / Upgrade tab unmounts this BuilderEditor;
   * if the Tour were mounted here, it would unmount mid-tour).
   */
  onPreviewReady?: () => void;
}

/**
 * Tenant-scoped Builder embedded inside the Portal. Replaces the
 * Portal's prior inline launcher editor with the full Builder UI
 * (theme, tiles, featured, schedules, OEM, telemetry, remote config,
 * etc.) that previously lived at build.mobilauncher.com.
 *
 * I/O:
 *   - Seeds from `initialConfig` (the caller fetches it via
 *     `fetchConfig(tenantId)` before mounting).
 *   - Every config patch debounces a `saveTenantConfig` PUT to the
 *     licensing-server, scoped by `editToken`.
 *   - Save status surfaces in the Toolbar so the user knows whether
 *     their last edit landed.
 *
 * Tier-gating: sections render with a TierLockBanner when the
 * customer's `currentTier` is below the section's `requires`. The
 * editor is still INTERACTIVE inside locked sections — by design, so
 * Free tenants can preview Pro themes / Enterprise OEM features and
 * see what they'd unlock. The runtime is the enforcement point.
 */
export function BuilderEditor({
  tenantId,
  editToken,
  initialConfig,
  currentTier,
  onUpgradeClick,
  onPreviewReady,
}: BuilderEditorProps): JSX.Element {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: 'idle' });
  // Subnet groupings derived from the tenant's live device IPs — feeds the
  // per-tile pilot editor's subnet picker. Best-effort: a failed/forbidden
  // fetch just leaves the picker empty (the editor then prompts to set a
  // date or use percentage instead).
  const [pilotSubnets, setPilotSubnets] = useState<readonly SubnetOption[]>([]);
  const [pilotTags, setPilotTags] = useState<readonly string[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightSave = useRef<number>(0);
  /**
   * Mirror of the latest config we were asked to persist. The unmount
   * cleanup reads this — closures captured at mount-time saw only
   * `initialConfig`, which meant a tab switch with a pending debounce
   * wrote the INITIAL config back to the server and stomped the very
   * edit the cleanup was trying to flush. ([config] useEffect in state.ts
   * fires onPersist with the latest config every time, so updating the
   * ref here is the natural place to stash it.)
   */
  const latestConfigRef = useRef<Config>(initialConfig);

  useEffect(() => {
    let cancelled = false;
    void listDevices({ tenantId, editToken }).then((res) => {
      if (cancelled || res.kind !== 'ok') return;
      // Subnet rules match each device's own LAN IPv4, so group by that.
      setPilotSubnets(deriveSubnetOptions(res.devices.map((d) => d.localIpv4)));
      setPilotTags(deriveTagOptions(res.devices.map((d) => d.metadata)));
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, editToken]);

  const persistConfig = useCallback(
    (config: Config) => {
      latestConfigRef.current = config;
      // Surface "Unsaved" immediately so the user knows there's a
      // debounced PUT in flight and shouldn't reload mid-window. The
      // beforeunload handler below catches reloads that happen before
      // the debounce fires, but the status pill is the first line of
      // defense — visual feedback within the same frame as the click.
      setSaveStatus({ kind: 'pending' });
      if (saveTimer.current !== null) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        const seq = ++inFlightSave.current;
        setSaveStatus({ kind: 'saving' });
        void saveTenantConfig({ tenantId, editToken, config: config as unknown as Record<string, unknown> }).then(
          (res: SaveConfigResult) => {
            // Drop stale results — only the most recent save's outcome
            // is meaningful to the user. Older saves that landed get
            // their result discarded; the latest call determines status.
            if (seq !== inFlightSave.current) return;
            if (res.kind === 'ok') {
              setSaveStatus({ kind: 'saved', at: Date.now() });
            } else if (res.kind === 'unauthorized') {
              setSaveStatus({ kind: 'error', message: 'Edit token rejected' });
            } else if (res.kind === 'invalid') {
              const first = res.issues[0];
              setSaveStatus({
                kind: 'error',
                message: first ? `${first.path.join('.')}: ${first.message}` : 'Config invalid',
              });
            } else {
              const msg = 'message' in res ? res.message : 'Save failed';
              setSaveStatus({ kind: 'error', message: msg });
            }
          },
        );
      }, SAVE_DEBOUNCE_MS);
    },
    [tenantId, editToken],
  );

  const store = useBuilderStore({ initialConfig, onPersist: persistConfig });
  const [active, setActive] = useState<SectionId>('profile-basics');
  const activeSection = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0]!;
  const [previewWidth, setPreviewWidth] = useState<number>(loadPreviewWidth);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useEffect(() => {
    savePreviewWidth(previewWidth);
  }, [previewWidth]);

  // Global keyboard shortcuts — same model as the standalone Builder.
  // Cmd/Ctrl+Z = config-level undo (eats native field-level undo).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        store.undo();
        return;
      }
      if (mod && ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        store.redo();
        return;
      }
      if (e.key === '?' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [store]);

  // Flush pending save on unmount so the user doesn't lose their last
  // ~800ms of edits when they navigate away. Reads from latestConfigRef
  // — `store.config` in this closure would be the snapshot from when
  // the effect was set up (mount-time = initialConfig), which would
  // OVERWRITE the user's edit with the original on every tab switch.
  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        const seq = ++inFlightSave.current;
        void saveTenantConfig({
          tenantId,
          editToken,
          config: latestConfigRef.current as unknown as Record<string, unknown>,
        }).then(() => {
          void seq;
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Page-reload / tab-close flush. Unmount cleanup only runs on
  // React-driven unmount (tab switch inside the SPA) — a hard reload or
  // closing the tab fires `beforeunload` instead. Without this, clicking
  // "Enable all" then immediately reloading lost the edit: the 800ms
  // debounce hadn't fired yet, and the PUT never went out.
  //
  // `keepalive: true` on the fetch lets the browser finish the request
  // even after the page is gone — bounded at 64 KB request size, which
  // is plenty for a launcher config (typical ~5-15 KB serialized).
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      if (saveTimer.current === null) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try {
        void fetch(
          `${apiBaseUrl}/tenant/configs/${encodeURIComponent(tenantId)}`,
          {
            method: 'PUT',
            // credentials so a non-owner admin (no editToken) authorizes via the
            // session cookie; bearer only when we have a token.
            credentials: 'include',
            headers: {
              'content-type': 'application/json',
              ...(editToken !== null ? { authorization: `Bearer ${editToken}` } : {}),
            },
            body: JSON.stringify(latestConfigRef.current),
            keepalive: true,
          },
        );
      } catch {
        // Can't observe the result; if it fails we've at least warned
        // the user via preventDefault below.
      }
      // Show the browser's native "Leave site?" prompt so the user has
      // a chance to wait the half-second the save needs. Modern browsers
      // ignore custom messages; the prompt itself is the value here.
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, editToken]);

  return (
    <PilotSubnetsContext.Provider value={pilotSubnets}>
    <PilotTagsContext.Provider value={pilotTags}>
    <div
      className="builder builder--embedded"
      style={{ '--preview-width': `${previewWidth}px` } as CSSProperties}
    >
      <Toolbar
        store={store}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        embeddedInPortal
      />
      <ProfileBar store={store} />
      <SaveStatusStrip status={saveStatus} />

      <div className="builder__body">
        <nav className="builder__nav" aria-label="Sections">
          <ul className="builder__nav-list">
            {SECTIONS.map((s) => {
              const locked = TIER_RANK[s.requires] > TIER_RANK[currentTier];
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`builder__nav-item builder__nav-item--${s.status}${
                      active === s.id ? ' builder__nav-item--active' : ''
                    }${locked ? ' builder__nav-item--locked' : ''}`}
                    disabled={s.status === 'planned'}
                    onClick={() => setActive(s.id)}
                    data-testid={`builder-nav-${s.id}`}
                  >
                    <span>{s.label}</span>
                    {s.status === 'planned' && (
                      <span className="builder__nav-tag">soon</span>
                    )}
                    {locked && (
                      <span
                        className="builder__nav-tag builder__nav-tag--lock"
                        aria-label={`Requires ${TIER_LABEL[s.requires]}`}
                      >
                        🔒
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className="builder__editor" tabIndex={-1}>
          <SectionLockGate
            section={activeSection}
            currentTier={currentTier}
            onUpgradeClick={onUpgradeClick}
          >
            {active === 'profile-basics' && <ProfileBasicsEditor store={store} />}
            {active === 'tiles' && <TileGridEditor store={store} />}
            {active === 'hero' && <HeroEditor store={store} />}
            {active === 'tools' && <ToolsEditor store={store} />}
            {active === 'theme' && <ThemeEditor store={store} />}
            {active === 'status-bar' && <StatusBarEditor store={store} />}
            {active === 'admin' && <AdminAccessEditor store={store} />}
            {active === 'features' && <FeaturesEditor store={store} />}
            {active === 'schedules' && <SchedulesEditor store={store} />}
            {active === 'signage' && <SignageEditor store={store} />}
            {active === 'device' && <OemDeviceEditor store={store} />}
            {active === 'telemetry' && <TelemetryEditor store={store} />}
            {active === 'remote' && <RemoteConfigEditor store={store} />}
          </SectionLockGate>
        </main>

        <Splitter width={previewWidth} onChange={setPreviewWidth} />

        <PreviewPane
          config={store.config}
          parseStatus={store.parseStatus}
          onJumpTo={(target: JumpTarget) => {
            setActive(target.section);
            if (target.profileIndex !== undefined) {
              const p = store.config.profiles[target.profileIndex];
              if (p) store.setActiveProfileId(p.id);
            }
          }}
          {...(onPreviewReady !== undefined && { onPreviewReady })}
        />
      </div>

      <ShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {/*
        First-run editor tour lives in ConfigEditor (above the outer-
        tab boundary) so a step that switches to Broadcasts / Devices /
        Upgrade doesn't unmount this BuilderEditor along with the
        Joyride instance inside it — which would kill the tour
        mid-step. ConfigEditor reads the previewReady signal via the
        onPreviewReady callback above.
      */}
    </div>
    </PilotTagsContext.Provider>
    </PilotSubnetsContext.Provider>
  );
}

function SaveStatusStrip({ status }: { status: SaveStatus }): JSX.Element | null {
  if (status.kind === 'idle') return null;
  const label =
    status.kind === 'pending'
      ? 'Unsaved changes — auto-saving…'
      : status.kind === 'saving'
        ? 'Saving…'
        : status.kind === 'saved'
          ? `Saved`
          : `Save failed — ${status.message}`;
  const tone = status.kind === 'error' ? 'err' : status.kind === 'saved' ? 'ok' : 'pending';
  return (
    <div className={`builder__save-strip builder__save-strip--${tone}`} role="status" aria-live="polite">
      {label}
    </div>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Wraps the active section's content with a lock banner + dimmed
 * overlay when the customer's tier is below what the section requires.
 * Editing stays interactive — by design, so the live preview can still
 * simulate Pro/Ent features for Free customers. The runtime is the
 * enforcement point; this is purely a discovery + upsell affordance.
 */
function SectionLockGate({
  section,
  currentTier,
  onUpgradeClick,
  children,
}: {
  section: SectionDef;
  currentTier: Tier;
  onUpgradeClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const locked = TIER_RANK[section.requires] > TIER_RANK[currentTier];
  if (!locked) return <>{children}</>;
  return (
    <div className="builder__locked" data-testid={`section-locked-${section.id}`}>
      <div className={`builder__lock-banner builder__lock-banner--${section.requires}`}>
        <span className="builder__lock-badge">🔒 {TIER_LABEL[section.requires]}</span>
        <span className="builder__lock-text">
          Edits here won't apply on-device until you upgrade. The preview
          still simulates them so you can see what you'd unlock.
        </span>
        <button
          type="button"
          className="builder__lock-cta"
          onClick={onUpgradeClick}
          data-testid={`section-lock-upgrade-${section.id}`}
        >
          Upgrade →
        </button>
      </div>
      <div className="builder__locked-content">{children}</div>
    </div>
  );
}
